import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { livestreamService } from '../../services/livestream';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import Alert from '../../components/Common/Alert';
import { ImageUtils } from '../../utils/imageUtils';

const LivestreamList = () => {
  const navigate = useNavigate();
  const [livestreams, setLivestreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'created_at'
  });

  const loadLivestreams = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.sortBy) params.ordering = filters.sortBy === 'created_at' ? '-created_at' : `-${filters.sortBy}`;

      const data = await livestreamService.getMyLivestreams(params);
      setLivestreams(data.results || data);
    } catch (error) {
      console.error('Error loading livestreams:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load livestreams. Please refresh the page.'
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadLivestreams();
  }, [loadLivestreams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadLivestreams();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, loadLivestreams]);

  const handleDelete = async (livestreamId, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await livestreamService.deleteLivestream(livestreamId);
        setLivestreams(livestreams.filter(l => l.id !== livestreamId));
        setAlert({
          type: 'success',
          message: 'Livestream deleted successfully!'
        });
      } catch (error) {
        setAlert({
          type: 'danger',
          message: 'Failed to delete livestream. Please try again.'
        });
      }
    }
  };

  const handleStartLivestream = async (livestreamId) => {
    try {
      await livestreamService.startLivestream(livestreamId);
      loadLivestreams();
      setAlert({
        type: 'success',
        message: 'Livestream started successfully!'
      });
    } catch (error) {
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to start livestream. Please try again.'
      });
    }
  };

  const handleEndLivestream = async (livestreamId) => {
    if (window.confirm('Are you sure you want to end this livestream?')) {
      try {
        await livestreamService.endLivestream(livestreamId);
        loadLivestreams();
        setAlert({
          type: 'success',
          message: 'Livestream ended successfully!'
        });
      } catch (error) {
        setAlert({
          type: 'danger',
          message: 'Failed to end livestream. Please try again.'
        });
      }
    }
  };

  const handleDuplicateLivestream = async (livestreamId, title) => {
    if (window.confirm(`Create a copy of "${title}"?`)) {
      try {
        setLoading(true);
        const newLivestream = await livestreamService.duplicateLivestream(livestreamId);

        setAlert({
          type: 'success',
          message: 'Livestream duplicated successfully!'
        });

        await loadLivestreams();

        setTimeout(() => {
          navigate(`/livestreams/${newLivestream.id}/edit`);
        }, 1500);
      } catch (error) {
        console.error('Error duplicating livestream:', error);
        setAlert({
          type: 'danger',
          message: error.response?.data?.error || 'Failed to duplicate livestream. Please try again.'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      live: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', text: 'Live', pulse: true },
      scheduled: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', text: 'Scheduled' },
      ended: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)', text: 'Ended' }
    };

    const config = statusConfig[status] || statusConfig.scheduled;

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${config.pulse ? 'animate-pulse' : ''}`} style={{ backgroundColor: config.bg, color: config.color }}>
        {config.text}
      </span>
    );
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      status: '',
      sortBy: 'created_at'
    });
  };

  const hasActiveFilters = () => {
    return filters.search || filters.status || filters.sortBy !== 'created_at';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen p-3 md:p-4 lg:p-6" style={{ backgroundColor: '#0a0f1e' }}>
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header with Gradient */}
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-dark-800/60 via-dark-800/40 to-transparent backdrop-blur-xl border border-white/5 p-5 shadow-2xl">
          {/* Animated gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 via-transparent to-purple-500/5 opacity-50"></div>

          <div className="relative flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500/20 rounded-xl blur-lg"></div>
                <div className="relative w-12 h-12 bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-xl flex items-center justify-center border border-red-500/30 shadow-lg shadow-red-500/10">
                  <i className="bi bi-broadcast text-red-500 text-xl"></i>
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-black text-white tracking-tight uppercase mb-0.5 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">My Livestreams</h2>
                <p className="text-gray-500 text-[10px] font-bold uppercase tracking-[0.2em]">
                  Manage your live shopping sessions
                </p>
              </div>
            </div>
            <Link
              to="/livestreams/create"
              className="group px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 font-bold text-xs uppercase tracking-widest shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:scale-105 inline-flex items-center gap-2"
            >
              <i className="bi bi-plus-lg group-hover:rotate-90 transition-transform duration-300"></i>
              Create Livestream
            </Link>
          </div>
        </div>

        {alert && (
          <div className="mb-6">
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          </div>
        )}

        {/* Enhanced Filters */}
        <div className="bg-dark-800/40 backdrop-blur-xl rounded-2xl border border-white/5 p-4 mb-6 shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center border border-purple-500/20">
              <i className="bi bi-funnel text-purple-500 text-sm"></i>
            </div>
            <div>
              <h4 className="text-xs font-bold text-white tracking-widest uppercase">Filter & Search</h4>
              <p className="text-gray-600 text-[10px] font-bold uppercase tracking-[0.15em]">Refine Your Results</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className="block text-[10px] font-bold uppercase tracking-widest mb-2 text-gray-500">Search</label>
              <div className="relative group">
                <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 group-hover:text-red-500 transition-colors text-sm"></i>
                <input
                  type="text"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl text-sm outline-none bg-dark-900/50 text-white border border-white/5 focus:border-red-500/30 transition-all placeholder-gray-700"
                  placeholder="Search livestreams..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest mb-2 text-gray-500">Status</label>
              <div className="relative">
                <select
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none appearance-none bg-dark-900/50 text-white border border-white/5 focus:border-red-500/30 transition-all cursor-pointer"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">All Status</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="live">Live</option>
                  <option value="ended">Ended</option>
                </select>
                <i className="bi bi-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none"></i>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest mb-2 text-gray-500">Sort By</label>
              <div className="relative">
                <select
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none appearance-none bg-dark-900/50 text-white border border-white/5 focus:border-red-500/30 transition-all cursor-pointer"
                  value={filters.sortBy}
                  onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                >
                  <option value="created_at">Latest</option>
                  <option value="scheduled_start_time">Schedule</option>
                  <option value="title">Title</option>
                </select>
                <i className="bi bi-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none"></i>
              </div>
            </div>
          </div>
          {hasActiveFilters() && (
            <div className="flex justify-end mt-4 pt-4 border-t border-white/5">
              <button
                className="group text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 text-red-500 hover:text-red-400 transition-all px-3 py-1.5 rounded-lg hover:bg-red-500/10"
                onClick={resetFilters}
              >
                <i className="bi bi-x-circle group-hover:rotate-90 transition-transform"></i>
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Enhanced Livestreams Grid */}
        {livestreams.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {livestreams.map((livestream) => (
              <div key={livestream.id} className="group bg-dark-800/40 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/5 hover:border-red-500/20 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-red-500/10">
                {/* Thumbnail */}
                <div className="relative aspect-[3/4] overflow-hidden">
                  {livestream.thumbnail ? (
                    <img
                      src={ImageUtils.getFullImageUrl(livestream.thumbnail)}
                      className="w-full h-full object-cover"
                      alt={livestream.title}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: '#0f1420' }}>
                      <i className="bi bi-broadcast text-6xl" style={{ color: '#64748b' }}></i>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="absolute top-3 right-3">
                    {getStatusBadge(livestream.status)}
                  </div>

                  {/* Viewer Count */}
                  {livestream.viewer_count > 0 && (
                    <div className="absolute bottom-3 left-3 px-3 py-1 rounded-lg flex items-center gap-2" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(10px)' }}>
                      <i className="bi bi-people-fill text-sm" style={{ color: '#ef4444' }}></i>
                      <span className="text-white text-sm font-bold">{livestream.viewer_count}</span>
                    </div>
                  )}

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h5 className="text-sm font-bold text-white mb-1.5 line-clamp-1 group-hover:text-red-500 transition-colors">{livestream.title}</h5>
                  <p className="text-xs mb-3 line-clamp-2 text-gray-500 leading-relaxed">
                    {livestream.description || 'No description'}
                  </p>

                  <div className="flex items-center gap-1.5 mb-3 text-[10px] text-gray-600 font-bold uppercase tracking-widest">
                    <i className="bi bi-calendar text-[9px]"></i>
                    <span>{formatDate(livestream.scheduled_start_time || livestream.created_at)}</span>
                  </div>

                  <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)', paddingTop: '12px' }}>
                    {/* Scheduled Actions */}
                    {livestream.status === 'scheduled' && (
                      <div className="flex gap-2">
                        <button
                          className="flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                          style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)' }}
                          onClick={() => handleStartLivestream(livestream.id)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef4444';
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                            e.currentTarget.style.color = '#ef4444';
                          }}
                        >
                          Start
                        </button>
                        <button
                          className="px-3 py-2 rounded-lg transition-all"
                          style={{ backgroundColor: '#0f1420', color: '#64748b' }}
                          onClick={() => navigate(`/livestreams/${livestream.id}/edit`)}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="px-3 py-2 rounded-lg transition-all"
                          style={{ backgroundColor: '#0f1420', color: '#64748b' }}
                          onClick={() => handleDelete(livestream.id, livestream.title)}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    )}

                    {/* Live Actions */}
                    {livestream.status === 'live' && (
                      <div className="flex gap-2">
                        <Link
                          to={`/livestreams/${livestream.id}/control`}
                          className="flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-center transition-all"
                          style={{ backgroundColor: '#ef4444', color: '#fff' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                        >
                          Control Panel
                        </Link>
                        <button
                          className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                          style={{ backgroundColor: '#0f1420', color: '#ef4444' }}
                          onClick={() => handleEndLivestream(livestream.id)}
                        >
                          End
                        </button>
                      </div>
                    )}

                    {/* Ended Actions */}
                    {livestream.status === 'ended' && (
                      <div className="flex gap-2">
                        <Link
                          to={`/livestreams/${livestream.id}/details`}
                          className="flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-center transition-all"
                          style={{ backgroundColor: '#0f1420', color: '#fff' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1a2332'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0f1420'}
                        >
                          View Details
                        </Link>
                        <button
                          className="px-3 py-2 rounded-lg transition-all"
                          style={{ backgroundColor: '#0f1420', color: '#64748b' }}
                          onClick={() => handleDuplicateLivestream(livestream.id, livestream.title)}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                        >
                          <i className="bi bi-files"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl p-12 text-center" style={{ backgroundColor: '#161e35' }}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
              <i className="bi bi-broadcast text-4xl" style={{ color: '#ef4444' }}></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">No Livestreams Found</h3>
            <p className="text-sm mb-8 max-w-md mx-auto" style={{ color: '#64748b' }}>
              {hasActiveFilters()
                ? 'No livestreams match your filters. Try adjusting your search criteria.'
                : 'Create your first livestream to start selling live to your audience.'}
            </p>
            {!hasActiveFilters() && (
              <Link to="/livestreams/create" className="inline-flex items-center gap-2 px-8 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-all" style={{ backgroundColor: '#ef4444', color: '#fff' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}>
                <i className="bi bi-plus-lg"></i>
                Create First Livestream
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LivestreamList;
